import type { EmailAddress, EmailParticipant } from '@/gen/cortado/v1/emails_pb'

/**
 * The addresses of one kind on a message. The backend stores the kind as its
 * enum name ("From", "To", "Cc"), so the match ignores case.
 */
export function emailAddressesOfType(addresses: readonly EmailAddress[], type: string): string[] {
  const wanted = type.toLowerCase()
  return addresses
    .filter((address) => address.emailAddressType.toLowerCase() === wanted)
    .map((address) => address.emailAddress)
}

/**
 * The thread's customer addresses, which a reply goes to. The server derives
 * the same list itself; this is only what the read-only To line shows.
 */
export function threadCustomerAddresses(participants: readonly EmailParticipant[] | undefined): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const participant of participants ?? []) {
    const email = participant.email.trim()
    if (participant.type !== 'customer' || !email || seen.has(email.toLowerCase())) continue
    seen.add(email.toLowerCase())
    out.push(email)
  }
  return out
}

/** One person on a message: the name to show (may be empty) and the address. */
export interface EmailPerson {
  name: string
  email: string
}

/**
 * The contact a conversation belongs to, which names its addresses when
 * nothing else does. `emails` are the contact's addresses when the caller has
 * them loaded (the job page); without them only `name` is used, for the one
 * customer the thread leaves unnamed.
 */
export interface EmailContactFallback {
  name?: string
  emails?: readonly string[]
}

/** Participants of these kinds are the org's own people, never the contact. */
const TEAMMATE_PARTICIPANT_TYPES = new Set(['user'])

/**
 * The one resolver for the name to show beside an address on a thread, in
 * order: the name the address was sent with (`displayName`), the thread
 * participant with that address, the contact (when the address is one of the
 * contact's, case-insensitively), else empty so callers show the bare address.
 *
 * Without contact addresses, the contact's name goes to the single
 * non-teammate participant the thread leaves unnamed; with two or more such
 * participants there is no telling which is the contact, so none gets it.
 */
export function createEmailNameResolver(
  participants: readonly EmailParticipant[] | undefined,
  contact?: EmailContactFallback,
): (email: string, displayName?: string) => string {
  const participantNames = new Map<string, string>()
  for (const participant of participants ?? []) {
    const name = participant.name?.trim()
    const key = participant.email.trim().toLowerCase()
    if (name && key && !participantNames.has(key)) participantNames.set(key, name)
  }

  const contactName = contact?.name?.trim() ?? ''
  const contactKeys = new Set<string>()
  if (contactName) {
    const contactEmails = (contact?.emails ?? []).map((email) => email.trim().toLowerCase()).filter(Boolean)
    if (contactEmails.length > 0) {
      for (const email of contactEmails) contactKeys.add(email)
    } else {
      const unnamed = new Set<string>()
      for (const participant of participants ?? []) {
        const key = participant.email.trim().toLowerCase()
        if (!key || TEAMMATE_PARTICIPANT_TYPES.has(participant.type.toLowerCase())) continue
        if (!participantNames.has(key)) unnamed.add(key)
      }
      if (unnamed.size === 1) contactKeys.add([...unnamed][0]!)
    }
  }

  return (email, displayName) => {
    const key = email.trim().toLowerCase()
    return (
      displayName?.trim() || participantNames.get(key) || (contactKeys.has(key) ? contactName : '') || ''
    )
  }
}

/**
 * The people of one kind ("From", "To", "Cc") on a message, named. A message
 * address carries the name it was sent with when the mail named one; older
 * rows and outbound recipients often do not, so the thread's participant with
 * the same address supplies the name, then the thread's contact (see
 * `createEmailNameResolver`), else it stays empty and callers show the bare
 * address.
 */
export function messagePeopleOfType(
  addresses: readonly EmailAddress[],
  type: string,
  participants: readonly EmailParticipant[] | undefined,
  contact?: EmailContactFallback,
): EmailPerson[] {
  const wanted = type.toLowerCase()
  const nameFor = createEmailNameResolver(participants, contact)
  const seen = new Set<string>()
  const out: EmailPerson[] = []
  for (const address of addresses) {
    if (address.emailAddressType.toLowerCase() !== wanted) continue
    const email = address.emailAddress.trim()
    const key = email.toLowerCase()
    if (!email || seen.has(key)) continue
    seen.add(key)
    out.push({ email, name: nameFor(email, address.displayName) })
  }
  return out
}

/** "Anne Young", else the bare address. */
export function emailPersonLabel(person: EmailPerson): string {
  return person.name || person.email
}

/** Up to two initials from the name ("AY"), else the address's first letter. */
export function emailPersonInitials(person: EmailPerson): string {
  const words = person.name.split(/\s+/).filter((word) => /\p{L}|\p{N}/u.test(word))
  if (words.length > 0) {
    const first = words[0]!.match(/\p{L}|\p{N}/u)?.[0] ?? ''
    const last = words.length > 1 ? (words[words.length - 1]!.match(/\p{L}|\p{N}/u)?.[0] ?? '') : ''
    return (first + last).toUpperCase()
  }
  return (person.email.match(/\p{L}|\p{N}/u)?.[0] ?? '?').toUpperCase()
}
